import { Injectable, Injector, NgZone } from '@angular/core';
import { OktaAuth } from '@okta/okta-auth-js';
import { RuntimeConfigService } from './runtime-config.service';
import { jwtDecode } from 'jwt-decode';
import { AppService } from './app.service';
import { Capacitor, CapacitorHttp, HttpOptions } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { Preferences } from '@capacitor/preferences';
import { UrlHelper } from '../helpers/url-helper';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { UpdateCheckService } from './mobile-app-update-check-service';
import { HybridStorageService } from '../app/services/hybrid-storage.service';
import { AuthStorageKeys, AuthStorageService } from '../app/services/auth-storage.service';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import BackgroundTokenRefresh from './background-token-refresh';
import { USER_SERVICE_TOKEN } from './user-service.service';
import { GlobalPollingService } from './global-polling.service';
import { LoginDebugService } from './login-debug.service';
import { MatDialog } from '@angular/material/dialog';
import { LoginDiagnosticsModalComponent } from '../app/apps/shared/login-diagnostics-modal/login-diagnostics-modal.component';
import { Device } from '@capacitor/device';
import { Network } from '@capacitor/network';
import { loginDebugAlert } from './login-debug-alert';

// Per-tab cooldown marker for the auto sign-in that inbound ?loginType= links trigger. Stamped
// (epoch ms) by HomeComponent when it starts a linked sign-in, cleared by LoginCallbackComponent
// when a login completes, so the cooldown only ever suppresses retries of an abandoned attempt.
export const LinkedLoginAttemptKey = 'linked-login-type-attempted';

export interface ExtendedEnvironment {
  production: boolean;
  apiUrl: string;
  oktaConfig: any;
  oktaLoginTypes: {
    [key: string]: any;
  };
}

@Injectable({
  providedIn: 'root'
})
export class OktaAuthService {
  private oktaAuth: OktaAuth;
  private oktaUserId: string;
  private oktaUserIdSubject = new BehaviorSubject<string>('');
  private isLoggingIn: boolean = false;
  private currentLoginType: string = 'Staff';
  private _mobileUpdateService?: UpdateCheckService;
  private _userService?: any; // Lazy-loaded to avoid circular dependency
  private _loginDebugService?: LoginDebugService; // Lazy-loaded to avoid circular dependency
  private _matDialog?: MatDialog; // Lazy-loaded to avoid circular dependency
  private apiUrl: string;
  // Shared in-flight refresh promise — prevents concurrent callers (guard + AppComponent)
  // from firing two refreshAccessToken calls with the same refresh token, which Okta
  // rejects as invalid_grant when refresh-token rotation is enabled.
  private refreshInFlight: Promise<boolean> | null = null;
  isHandlingRedirect = false;

  // Android only: true while the Okta login page is open in a Chrome Custom Tab (system browser).
  // Set when setLocation launches the tab; cleared when the callback deep link is handled (so the tab
  // is dismissed) or when the Custom Tab fails to open and we fall back to navigating the WebView.
  // The Android System WebView does not expose the WebAuthn (PublicKeyCredential) API, so rendering
  // Okta's sign-in there breaks passkey/biometric factors — Custom Tabs runs Chrome and supports them.
  // iOS already routes Okta to SFSafariViewController natively (AppDelegate.PermanentWebViewDelegate),
  // and web navigates normally, so this path is Android-only.
  private isAuthBrowserOpen = false;

  // Set the moment a logout begins; blocks every token-persistence path (refresh, Keychain/native
  // sync) so an in-flight write started before logout can't re-seed storage AFTER we've cleared it.
  // Without this, a refresh/sync that resolves during logout repopulates Preferences + Keychain, and
  // the post-logout cold start reads the tokens back in — landing on /mainmenu with a dead session
  // (blank menu). Reset to false in initializeOktaAuth (fresh app start / picking a login type).
  private isLoggingOut = false;

  // Set when Okta definitively rejects the stored session during a refresh, BEFORE handleLogout runs.
  // handleLogout can stall when called from inside the refresh: its API calls 401 with the expired
  // access token, and the interceptor's 401 retry joins this same in-flight refresh, so neither
  // finishes and storage is never cleared. The mobile cold-start flow reads this to tell the user to
  // sign in again rather than offering a Retry that can never succeed.
  sessionRejected = false;

  // Token initialization tracking - prevents race condition where guard checks before tokens load
  private tokensInitialized = false;
  private initializationPromise: Promise<void> | null = null;

  constructor(
    private appService: AppService,
    private http: HttpClient,
    private injector: Injector,
    private router: Router,
    private zone: NgZone,
    private authStorageService: AuthStorageService,
    private hybridStorageService: HybridStorageService,
    private runtimeConfig: RuntimeConfigService,
    private globalPollingService: GlobalPollingService
  ) {
    this.apiUrl = this.runtimeConfig.currentConfig.apiUrl + '/OktaAuth/handleOktaTokenExchange';

    // Subscribe to demo mode changes to ensure Okta reinitializes when toggling demo mode
    this.runtimeConfig.isDemoMode$.subscribe(() => {
      // Reinitialize Okta with new configuration if already initialized
      if (this.oktaAuth) {
        this.initializeOktaAuth(this.currentLoginType);
      }
    });

    // Setup cross-tab token sync for web platform
    this.setupCrossTabTokenSync();
  }

  /**
   * Setup cross-tab token synchronization
   * Listen for localStorage changes from other tabs and reload tokens
   * Web only - mobile doesn't have multiple tabs
   */
  private setupCrossTabTokenSync(): void {
    if (Capacitor.isNativePlatform()) {
      return; // Mobile doesn't need cross-tab sync
    }

    // Listen for storage events from other tabs
    window.addEventListener('storage', async (event: StorageEvent) => {
      // Check if the change is related to Okta tokens
      if (event.key && event.key.startsWith('okta-token-storage')) {
        console.warn('[OktaAuth] Token change detected in another tab, reloading tokens');

        try {
          // Force reload tokens from localStorage
          if (this.oktaAuth) {
            await this.oktaAuth.tokenManager.get('accessToken');
            await this.oktaAuth.tokenManager.get('refreshToken');
          }
        } catch (error) {
          console.error('[OktaAuth] Failed to sync tokens from other tab:', error);
        }
      }
    });
  }

  private get mobileUpdateService(): UpdateCheckService {
    if (!this._mobileUpdateService) {
      this._mobileUpdateService = this.injector.get(UpdateCheckService);
    }
    return this._mobileUpdateService;
  }

  private get userService(): any {
    if (!this._userService) {
      this._userService = this.injector.get(USER_SERVICE_TOKEN);
    }
    return this._userService;
  }

  private get loginDebugService(): LoginDebugService {
    if (!this._loginDebugService) {
      this._loginDebugService = this.injector.get(LoginDebugService);
    }
    return this._loginDebugService;
  }

  private get matDialog(): MatDialog {
    if (!this._matDialog) {
      this._matDialog = this.injector.get(MatDialog);
    }
    return this._matDialog;
  }

  async initialize(): Promise<void> {
    // Store the initialization promise so waitForTokensInitialized() can wait for it
    if (!this.initializationPromise) {
      this.initializationPromise = this.performInitialization();
    }
    return this.initializationPromise;
  }

  private async performInitialization(): Promise<void> {
    try {
      // Critical: Wait for storage to be truly ready before proceeding
      // On mobile, Capacitor Preferences loads asynchronously - must complete before Okta init
      // If storage isn't ready, getItem() returns null even if tokens exist
      // The 'ready' property triggers loadFromPreferences() and waits for completion

      // Add timeout to prevent hanging forever if storage is completely corrupted
      const readyWithTimeout = Promise.race([
        this.authStorageService.ready,
        new Promise(resolve => setTimeout(resolve, 3000)) // 3 second timeout
      ]);
      await readyWithTimeout;

      let retries = 0;
      const maxRetries = 80; // 8 seconds for slower devices (iPad 6th gen, iPhone 8)
      const retryDelay = 100;

      while (!this.authStorageService.isStorageReady && retries < maxRetries) {
        if (retries === 0) {
          console.warn(`[OktaAuth] Waiting for storage to load...`);
        }
        if (retries % 20 === 0 && retries > 0) {
          console.warn(`[OktaAuth] Storage still loading after ${retries * retryDelay}ms (${retries}/${maxRetries})`);
        }
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        retries++;
      }

      if (!this.authStorageService.isStorageReady) {
        console.error(`[OktaAuth] CRITICAL: Storage failed to load after ${maxRetries * retryDelay}ms. Device storage may be corrupted or extremely slow.`);
        // Don't throw - app continues but will likely require re-login
      } else if (retries > 0) {
        
      }

      this.authStorageService.removeItem(AuthStorageKeys.Okta_CacheStorage);
      this.currentLoginType = this.authStorageService.getItem(AuthStorageKeys.Okta_LoginType) || 'Staff';
      this.initializeOktaAuth(this.currentLoginType);

      // Verify tokens were loaded after Okta initialization
      await this.verifyTokensLoaded();

      // Mark tokens as initialized - guards can now safely check
      this.tokensInitialized = true;
    } catch (error) {
      // Always mark as initialized even on error to prevent deadlock
      this.tokensInitialized = true;
      console.error('[OktaAuth] Initialization failed:', error);
      throw error;
    } finally {
      // Clear the promise so future calls to initialize() create a new one if needed
      this.initializationPromise = null;
    }
  }

  /**
   * Wait for tokens to be initialized before checking authentication
   * Prevents race condition where guard checks before storage/tokens are loaded
   */
  async waitForTokensInitialized(): Promise<void> {
    if (this.tokensInitialized) {
      return; // Already initialized
    }

    // If initialization is in progress, wait for it
    if (this.initializationPromise) {
      await this.initializationPromise;
      return;
    }

    // Wait up to 3 seconds for initialization to complete
    const maxWait = 3000;
    const checkInterval = 100;
    let waited = 0;

    while (!this.tokensInitialized && waited < maxWait) {
      await new Promise(resolve => setTimeout(resolve, checkInterval));
      waited += checkInterval;
    }

    if (!this.tokensInitialized) {
      console.warn('[OktaAuth] Token initialization did not complete within timeout');
    }
  }

   // Helper method to initialize OktaAuth with proper configuration
  private initializeOktaAuth(loginType: string): void {
    // A fresh OktaAuth instance means a fresh session lifecycle — re-enable token persistence that a
    // prior logout may have blocked (covers picking a login type after a logout without a reload).
    this.isLoggingOut = false;
    this.sessionRejected = false;

    const config = this.runtimeConfig.currentConfig;
    const loginTypeConfig = config.oktaLoginTypes[loginType];

    if (!loginTypeConfig) {
      
      // Fall back to a default login type if the requested one doesn't exist
      this.currentLoginType = loginType;
    }

    // Create a complete Okta config
    const oktaAuthConfig = {
      clientId: config.oktaLoginTypes[loginType].clientId,
      issuer: config.oktaLoginTypes[loginType].issuer,
      redirectUri: config.oktaLoginTypes[loginType].redirectUri,
      signOutRedirectURI: config.oktaLoginTypes[loginType].signOutRedirectURI,
      pkce: config.oktaConfig.pkce,
      scopes: config.oktaConfig.scopes,
      testing: config.oktaConfig.testing,
      // Override the SDK default (window.location.replace(originalUri)) so handleRedirect
      // does not full-reload to the page that initiated signInWithRedirect (the home / role
      // selector). LoginCallbackComponent handles post-auth navigation via the Angular Router.
      restoreOriginalUri: async () => { },
      // Android only: intercept the authorize redirect the SDK would otherwise run via
      // window.location.assign (which loads Okta inside the System WebView, where WebAuthn/passkeys
      // are unavailable) and open it in a Chrome Custom Tab instead. The SDK still does all PKCE /
      // transaction persistence before calling setLocation, so the existing callback token exchange
      // (handleOktaRedirect) is unchanged. iOS/web omit setLocation and keep their current behavior.
      ...(Capacitor.getPlatform() === 'android'
        ? { setLocation: (loc: string) => this.launchAuthInCustomTab(loc) }
        : {}),
      tokenManager: {
        // Mobile: Use authStorageService (native Capacitor Preferences)
        // Web: Use localStorage directly (simpler, faster, more reliable)
        storage: Capacitor.isNativePlatform() ? {
          getItem: (key: string) => this.authStorageService.getItem(key),
          setItem: (key: string, value: string) => this.authStorageService.setItem(key, value),
          removeItem: (key: string) => this.authStorageService.removeItem(key)
        } : {
          // Web: Use native localStorage directly for persistence
          getItem: (key: string) => localStorage.getItem(key),
          setItem: (key: string, value: string) => localStorage.setItem(key, value),
          removeItem: (key: string) => localStorage.removeItem(key)
        }
      },
      transactionManager: {
        // Mobile: Use authStorageService (native Capacitor Preferences)
        // Web: Use localStorage directly
        storage: Capacitor.isNativePlatform() ? {
          getItem: (key: string) => this.authStorageService.getItem(key),
          setItem: (key: string, val: string) => this.authStorageService.setItem(key, val),
          removeItem: (key: string) => this.authStorageService.removeItem(key)
        } : {
          // Web: Use native localStorage directly for persistence
          getItem: (key: string) => localStorage.getItem(key),
          setItem: (key: string, val: string) => localStorage.setItem(key, val),
          removeItem: (key: string) => localStorage.removeItem(key)
        }
      }
    };

    // Initialize the OktaAuth instance with the complete config
    this.oktaAuth = new OktaAuth(oktaAuthConfig as any);
  }

  async handleAuthRequired(router: Router) {
    const currentUrl = router.url;
    const isNativePlatform = Capacitor.isNativePlatform();
    const isHomePage = currentUrl === '';

    if (isHomePage) {
      return;
    }

    // Show diagnostics if debug mode enabled - user being redirected to login
    await this.showAuthRequiredDiagnostics(currentUrl);

    if (isNativePlatform) {
      if(!this.mobileUpdateService.isCriticalUpdateAvaliable)
      {
        this.setIsLoggingIn(true);
        this.zone.run(() => this.router.navigateByUrl(''));
      }
    } else {
      this.zone.run(() => this.router.navigateByUrl(''));
    }
  }

  getOktaAuth(): OktaAuth {
    return this.oktaAuth;
  }

  setOktaUserId(userId: string) {
    this.oktaUserId = userId;
    this.oktaUserIdSubject.next(this.oktaUserId);
  }

  get oktaUserId$(): Observable<string> {
    return this.oktaUserIdSubject.asObservable();
  }

  getIsLoggingIn(): boolean {
    return this.isLoggingIn;
  }

  setIsLoggingIn(isLoggingIn: boolean): void {
    this.isLoggingIn = isLoggingIn;
  }

  async getRedirectUriForLoginType(): Promise<string> {
    const config = this.runtimeConfig.currentConfig;
    const loginType = await this.authStorageService.getItem(AuthStorageKeys.Okta_LoginType) || 'Staff';
    return config.oktaLoginTypes[loginType]?.redirectUri || '';
  }

  // Methods for accessing login type configuration properties
  private getLoginTypeConfig(): any {
    const config = this.runtimeConfig.currentConfig;
    return config.oktaLoginTypes[this.currentLoginType];
  }

  private getClientId(): string {
    return this.getLoginTypeConfig()?.clientId;
  }

  private getIssuer(): string {
    return this.getLoginTypeConfig()?.issuer;
  }

  private getSignOutRedirectURI(): string {
    return this.getLoginTypeConfig()?.signOutRedirectURI;
  }

  private getAuthorizeUrl(): string {
    return this.getLoginTypeConfig()?.authorize;
  }

  private getTokenEndpoint(): string {
    return this.getLoginTypeConfig()?.token;
  }

  getUserInfoEndpoint(): string {
    return this.getLoginTypeConfig()?.userInfo;
  }

  updateOktaConfigForLoginType(loginType: string): void {
    this.currentLoginType = loginType;
    this.authStorageService.setItem(AuthStorageKeys.Okta_LoginType, loginType);

    // Re-initialize the OktaAuth instance with the new login type
    this.initializeOktaAuth(loginType);
  }

  // Used by mobile app
  async storeTokens(response: any) {
    // Mark tokens as not initialized during store to prevent guard race condition
    this.tokensInitialized = false;

    try {
      if (!response?.access_token || typeof response.access_token !== 'string') {
        throw new Error('Invalid token response: access_token is missing or not a string');
      }

      if (!response?.id_token || typeof response.id_token !== 'string') {
        throw new Error('Invalid token response: id_token is missing or not a string');
      }

      const decodedAccessToken = jwtDecode(response.access_token);
      const decodedIdToken = jwtDecode(response.id_token);

      const accessToken: any = {
        accessToken: response.access_token,
        expiresAt: Math.floor(Date.now() / 1000) + response.expires_in,
        tokenType: response.token_type,
        scopes: response.scope.split(' '),
        authorizeUrl: this.getAuthorizeUrl(),
        userinfoUrl: this.getUserInfoEndpoint(),
        claims: decodedAccessToken
      };

      const idToken: any = {
        idToken: response.id_token,
        expiresAt: Math.floor(Date.now() / 1000) + response.expires_in,
        scopes: response.scope.split(' '),
        issuer: this.getIssuer(),
        clientId: this.getClientId(),
        authorizeUrl: this.getAuthorizeUrl(),
        claims: decodedIdToken
      };

      // Refresh token expiry: use refresh_token_expires_in if provided, otherwise default to 7 days
      const refreshTokenExpiresIn = response.refresh_token_expires_in || (7 * 24 * 60 * 60); // 7 days in seconds
      const refreshToken: any = {
        refreshToken: response.refresh_token,
        expiresAt: Math.floor(Date.now() / 1000) + refreshTokenExpiresIn,
        scopes:  response.scope.split(' ')
      }

      // Use Okta SDK's tokenManager to store tokens - MUST await to ensure storage completes
      // CRITICAL: setTokens() is async and writes to storage (Keychain on iOS, EncryptedSharedPreferences on Android)
      // Not awaiting this causes race condition where Capgo can reload before tokens are persisted
      await this.oktaAuth.tokenManager.setTokens({
        accessToken: accessToken,
        idToken: idToken,
        refreshToken: refreshToken
      });

      // Additional safety delay to ensure async storage operations complete
      // tokenManager.setTokens() triggers storage writes that may not be fully flushed yet
      await new Promise(resolve => setTimeout(resolve, 100));

      // A login has completed and tokens are stored — clear the logged-out flag so cold-start/resume
      // session restore (including background-refreshed Keychain/native tokens) works normally again.
      await this.persistLoggedOut(false);

      // Save Okta config and tokens for iOS & Android background token refresh
      if (Capacitor.isNativePlatform()) {
        try {
          // Save config and schedule WorkManager (iOS & Android)
          await BackgroundTokenRefresh.saveOktaConfig({
            clientId: this.getClientId(),
            tokenUrl: this.getTokenEndpoint(),
            redirectUri: await this.getRedirectUriForLoginType()
          });

          // Seed the background-refresh token store (Android native storage / iOS Keychain) so the
          // OS background worker can refresh tokens and a later resume-sync has a fresh copy.
          await this.seedNativeBackgroundTokens(accessToken, idToken, refreshToken);
        } catch (error) {
          console.warn('[BG] Failed to save config/tokens for background refresh:', error);
        }
      }

      // Re-mark as initialized after store completes
      this.tokensInitialized = true;
    } catch (error) {
      // Always mark as initialized on error to prevent deadlock
      this.tokensInitialized = true;
      console.error('[OktaAuth] storeTokens failed:', error);
      throw error; // Re-throw so caller knows it failed
    }
  }

  // Ensure the access token is fresh. Returns true if a usable access token is in place
  // (either already valid, or refreshed successfully). Returns false only when there's no
  // path to a valid token (no refresh token, or the refresh call failed).
  //
  // This is the preferred entry point for "I need a usable token": it (1) short-circuits if the
  // access token is still valid — so a caller never refreshes a token someone else just refreshed,
  // (2) reads the CURRENT refresh token from storage rather than trusting a stale captured one, and
  // (3) shares the single-flight refresh in refreshAccessToken. Together these close the
  // concurrent/sequential double-spend that causes invalid_grant logouts under token rotation.
  async ensureFreshAccessToken(shouldLogRefresh: boolean = false): Promise<boolean> {
    // A logout is underway — don't start a refresh that would re-persist tokens we're clearing.
    if (this.isLoggingOut) {
      return false;
    }

    const accessToken: any = await this.oktaAuth.tokenManager.get('accessToken');
    if (accessToken && !this.oktaAuth.tokenManager.hasExpired(accessToken)) {
      return true;
    }

    const refreshToken: any = await this.oktaAuth.tokenManager.get('refreshToken');
    if (!refreshToken || this.oktaAuth.tokenManager.hasExpired(refreshToken)) {
      return false;
    }

    // refreshAccessToken owns the single-flight de-dup; just delegate.
    return this.refreshAccessToken(refreshToken, shouldLogRefresh);
  }

  // Check if we need to refresh the access token
  async refreshTokenIfNeeded() {
    // get the refresh token from the token manager
    const refreshToken = await this.oktaAuth.tokenManager.get('refreshToken');
    // if the refresh token is expired, log out the user, else refresh the access token
    if (refreshToken && !this.oktaAuth.tokenManager.hasExpired(refreshToken)) {
      const success = await this.refreshAccessToken(refreshToken);
      if (!success) {
        console.warn('[OktaAuth] Token refresh failed in refreshTokenIfNeeded');
        // Don't logout on failure - refreshAccessToken already handles auth errors
      }
    } else {
      await this.handleLogout('refreshTokenIfNeeded: Refresh token expired or missing', 'OktaAuthService.refreshTokenIfNeeded:347');
    }
  }

  // Sync tokens from Android native storage (called on app resume/cold start)
  async syncTokensFromNativeStorage(): Promise<boolean> {
    // Only for Android
    if (Capacitor.getPlatform() !== 'android') {
      return false;
    }

    // The user logged out — do not restore a session from native storage on this cold start/resume.
    // (Set in handleLogout, cleared on the next completed login in storeTokens.)
    if (await this.wasLoggedOut()) {
      return false;
    }

    // Mark tokens as not initialized during sync to prevent guard race condition
    this.tokensInitialized = false;

    try {
      const tokens = await BackgroundTokenRefresh.getRefreshedTokens();

      if (!tokens.accessToken || !tokens.idToken || !tokens.refreshToken) {
        return false;
      }

      // Android plugin returns already-parsed token objects (not JSON strings like iOS)
      // No need to JSON.parse() - tokens are ready to use
      const accessTokenData = tokens.accessToken;
      const idTokenData = tokens.idToken;
      const refreshTokenData = tokens.refreshToken;

      // Check if these tokens are different from what's in tokenManager
      const currentAccessToken: any = await this.oktaAuth.tokenManager.get('accessToken');
      const currentRefreshToken: any = await this.oktaAuth.tokenManager.get('refreshToken');

      // Recency guard: only adopt the native-storage copy if it is NEWER than what we already hold.
      // The WorkManager copy is seeded at login and re-written by the background refresh, but a
      // foreground refresh updates only the tokenManager — so the native copy can go stale. Without
      // this guard, a stale native token would clobber a fresher foreground-refreshed token and
      // trigger an invalid_grant logout. If what we have is at least as fresh, skip adoption — the
      // next foreground refresh re-seeds native storage anyway.
      const nativeAccessExpiry = accessTokenData?.expiresAt ?? 0;
      const currentAccessExpiry = currentAccessToken?.expiresAt ?? 0;
      if (currentAccessToken && nativeAccessExpiry <= currentAccessExpiry) {
        return false;
      }

      const accessTokenChanged = !currentAccessToken || currentAccessToken.accessToken !== accessTokenData.accessToken;
      const refreshTokenChanged = !currentRefreshToken || currentRefreshToken.refreshToken !== refreshTokenData.refreshToken;

      if (accessTokenChanged || refreshTokenChanged) {
        // Don't adopt native-storage tokens into the tokenManager if a logout is underway — it would
        // repopulate storage right after handleLogout cleared it.
        if (this.isLoggingOut) {
          return false;
        }

        // Update tokenManager with tokens from native storage
        // CRITICAL: Must await to ensure tokens are persisted before proceeding
        await this.oktaAuth.tokenManager.setTokens({
          accessToken: accessTokenData,
          idToken: idTokenData,
          refreshToken: refreshTokenData
        });

        // Safety delay for storage flush
        await new Promise(resolve => setTimeout(resolve, 100));

        return true;
      } else {
        return false;
      }
    } catch (error: any) {
      // Only log as error if it's NOT the expected "no tokens available" case
      if (error?.message?.includes('No tokens available') || error?.toString()?.includes('No tokens available')) {
        // This is expected on first login - no background tokens yet, not an error
        // Silent - don't log anything to avoid confusion
      } else {
        // Unexpected error - log as error
        console.error('[OktaAuth] Failed to sync tokens from Android native storage:', error);
      }
      return false;
    } finally {
      // Always mark as initialized after sync attempt (success or failure) to prevent deadlock
      this.tokensInitialized = true;
    }
  }

  // Single-flight entry point for token refresh. If a refresh is already running, every other
  // caller shares that same in-flight promise instead of starting a second one. This is essential
  // with refresh-token rotation: a concurrent second refresh would present a now-consumed refresh
  // token and get invalid_grant → forced logout. ALL refresh callers funnel through here (guard,
  // HTTP interceptor, cold start, resume, retry, reconnect, heartbeat, deep link).
  async refreshAccessToken(refreshToken: any, shouldLogRefresh: boolean = false): Promise<boolean> {
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }
    this.refreshInFlight = this.performTokenRefresh(refreshToken, shouldLogRefresh)
      .finally(() => { this.refreshInFlight = null; });
    return this.refreshInFlight;
  }

  // Issues a single refresh_token grant to Okta and returns the parsed response (throws on non-200
  // with { status, error } so the caller can branch on invalid_grant). redirect_uri is intentionally
  // OMITTED — per OAuth 2.0 it's only required for the auth-code exchange, and including it can cause
  // invalid_grant under strict matching. The native background workers (Swift/Java) match this.
  private async requestOktaTokenRefresh(tokenUrl: string, clientId: string, refreshTokenStr: string): Promise<any> {
    const params = new URLSearchParams({
      client_id: clientId,
      grant_type: 'refresh_token',
      refresh_token: refreshTokenStr
    });

    if (Capacitor.isNativePlatform()) {
      // Native: CapacitorHttp, bounded so a weak connection fails fast instead of hanging.
      const options: HttpOptions = {
        url: tokenUrl,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        data: params.toString(),
        connectTimeout: 8000,
        readTimeout: 8000
      };
      const httpResponse = await CapacitorHttp.post(options);

      if (httpResponse.status !== 200) {
        const errorObj: any = new Error(`Token refresh failed with status ${httpResponse.status}`);
        errorObj.status = httpResponse.status;
        errorObj.error = httpResponse.data;
        throw errorObj;
      }

      let response = httpResponse.data;
      if (typeof response === 'string') {
        response = JSON.parse(response);
      }
      return response;
    }

    // Web: Angular HttpClient (Observable → Promise)
    return await firstValueFrom(
      this.http.post(tokenUrl, params.toString(), {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      })
    );
  }

  // #1 (cross-process refresh lock). Best-effort mutex shared with the native background worker
  // (iOS Keychain / Android EncryptedSharedPreferences) so the foreground and the worker can't spend
  // the same rotating refresh token at once. Returns true when WE hold the lock. Native-only — on web
  // there is no second process, so always "acquired". Never blocks a refresh if the bridge errors.
  private async tryAcquireNativeRefreshLock(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      return true;
    }
    try {
      const res = await BackgroundTokenRefresh.tryAcquireRefreshLock();
      return !!res?.acquired;
    } catch (error) {
      console.warn('[OktaAuth] Refresh-lock acquire failed (proceeding without lock):', error);
      return true;
    }
  }

  private async releaseNativeRefreshLock(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    try {
      await BackgroundTokenRefresh.releaseRefreshLock();
    } catch (error) {
      console.warn('[OktaAuth] Refresh-lock release failed:', error);
    }
  }

  // Pull the freshest token the native background worker may have just written, and return its
  // refresh-token string IF it differs from the one we just tried. Used both proactively (we couldn't
  // get the lock) and reactively (#2: Okta returned invalid_grant — the worker likely rotated under
  // us). Returns null when nothing newer is available, signalling the caller to stop retrying.
  private async adoptNewerRefreshTokenFromNative(triedRefreshStr: string): Promise<string | null> {
    try {
      const platform = Capacitor.getPlatform();
      let synced = false;
      if (platform === 'ios') {
        synced = await this.syncTokensFromKeychain();
      } else if (platform === 'android') {
        synced = await this.syncTokensFromNativeStorage();
      }
      if (!synced) {
        return null;
      }
      const rt: any = await this.oktaAuth.tokenManager.get('refreshToken');
      if (rt?.refreshToken && rt.refreshToken !== triedRefreshStr) {
        return rt.refreshToken;
      }
      return null;
    } catch (error) {
      console.warn('[OktaAuth] Failed to adopt newer refresh token from native storage:', error);
      return null;
    }
  }

  // The actual token-refresh worker. Never call this directly — go through refreshAccessToken (or
  // ensureFreshAccessToken) so the single-flight guarantee holds.
  private async performTokenRefresh(refreshToken: any, shouldLogRefresh: boolean = false): Promise<boolean> {
    // Tracked here (not inside try) so the finally can release it on every exit path.
    let lockAcquired = false;
    try {
      // Mark tokens as not initialized during refresh to prevent guard race condition
      this.tokensInitialized = false;

      const oktaAuth = this.getOktaAuth();
      const tokenUrl = this.getTokenEndpoint(); // Okta's token endpoint
      const clientId = this.getClientId(); // Your Okta client ID

      // The refresh-token string we'll present. May be swapped for a newer one the background worker
      // produced (see lock-miss and invalid_grant handling below).
      let currentRefreshStr = refreshToken?.refreshToken;

      // #1 (cross-process refresh lock): don't let the foreground and the native background worker
      // spend the same rotating refresh token at once. If we can't get the lock the worker is
      // mid-refresh — adopt whatever it just wrote rather than racing it onto an invalid_grant.
      lockAcquired = await this.tryAcquireNativeRefreshLock();
      if (!lockAcquired) {
        const adopted = await this.adoptNewerRefreshTokenFromNative(currentRefreshStr);
        if (adopted) {
          console.warn('[OktaAuth] Refresh lock held by background worker — adopted its newer token');
          currentRefreshStr = adopted;
        }
      }

      // Send the request to Okta's token endpoint to get new tokens.
      // #2 (rotation-race recovery): if Okta answers invalid_grant, the background worker may have
      // just rotated the token out from under us. Re-sync the freshest token from native storage and
      // retry ONCE — only a SECOND invalid_grant means the refresh token is genuinely dead.
      let response: any;
      let resyncRetried = false;
      while (true) {
        try {
          response = await this.requestOktaTokenRefresh(tokenUrl, clientId, currentRefreshStr);
          break;
        } catch (httpErr: any) {
          if (Capacitor.isNativePlatform() && httpErr?.error?.error === 'invalid_grant' && !resyncRetried) {
            resyncRetried = true;
            const adopted = await this.adoptNewerRefreshTokenFromNative(currentRefreshStr);
            if (adopted) {
              console.warn('[OktaAuth] invalid_grant on refresh — adopted newer token from native storage, retrying once');
              currentRefreshStr = adopted;
              continue;
            }
          }
          throw httpErr; // network / 401-403 / genuine invalid_grant → handled by the outer catch
        }
      }

      // Extract new tokens from the response
      const { access_token, refresh_token, expires_in, token_type, id_token, refresh_token_expires_in } = response;
      const decodedAccessToken = jwtDecode(access_token);
      const decodedIdToken = jwtDecode(id_token);

      const accessTokenResult: any = {
        accessToken: access_token,
        expiresAt: Math.floor(Date.now() / 1000) + expires_in,
        tokenType: token_type,
        authorizeUrl: this.getAuthorizeUrl(),
        userinfoUrl: this.getUserInfoEndpoint(),
        claims: decodedAccessToken
      };

      // Refresh token expiry: use refresh_token_expires_in if provided, otherwise default to 7 days
      const refreshTokenExpiresIn = refresh_token_expires_in || (7 * 24 * 60 * 60); // 7 days in seconds
      const refreshTokenResult: any = {
        refreshToken: refresh_token,
        expiresAt: Math.floor(Date.now() / 1000) + refreshTokenExpiresIn
      };

      const idToken: any = {
        idToken: id_token,
        expiresAt: Math.floor(Date.now() / 1000) + expires_in,
        issuer: this.getIssuer(),
        clientId: this.getClientId(),
        authorizeUrl: this.getAuthorizeUrl(),
        claims: decodedIdToken
      };

      // A logout began while this refresh was in flight — discard the freshly minted tokens instead
      // of persisting them. Otherwise setTokens() + seedNativeBackgroundTokens() below would re-seed
      // Preferences/localStorage AND the Keychain/native store right after handleLogout cleared them,
      // and the post-logout cold start would read them back in (dead session, blank menu).
      if (this.isLoggingOut) {
        console.warn('[OktaAuth] Logout in progress — discarding refreshed tokens to prevent repopulation');
        this.tokensInitialized = true;
        return false;
      }

      // Save the new tokens in the token manager
      // CRITICAL: Must await to ensure async storage writes complete
      await oktaAuth.tokenManager.setTokens({
        idToken: idToken,
        accessToken: accessTokenResult,
        refreshToken: refreshTokenResult,
      });

      // Safety delay for storage flush
      await new Promise(resolve => setTimeout(resolve, 100));

      // this is rare but let's check for it - Okta's endpoint returns a token, but the user's session is invalid or revoked, and Okta still issues a token.
      if (!accessTokenResult || !accessTokenResult.expiresAt || (Math.floor(Date.now() / 1000) > accessTokenResult.expiresAt)) {

        // Re-mark as initialized before logout
        this.tokensInitialized = true;
        this.sessionRejected = true;
        await this.handleLogout('refreshAccessToken: Received invalid/expired access token from Okta', 'OktaAuthService.refreshAccessToken:498');
        return false;
      }

      // Keep the background-refresh token store current with this (possibly rotated) token, so the
      // OS background worker uses a live token and a later resume-sync can't clobber us with a stale
      // one. Applies to both iOS (Keychain) and Android (native storage).
      await this.seedNativeBackgroundTokens(accessTokenResult, idToken, refreshTokenResult);

      // Log TokenRefresh event if requested (cold start or app resume scenarios)
      if (shouldLogRefresh) {
        try {
          const rawDeviceInfo = await this.userService.getUserDeviceInfo();

          // Format device info with TokenRefresh authType
          let formattedDeviceInfo: any = {
            isMobile: rawDeviceInfo.isMobile,
            timestamp: new Date().toISOString(),
            authType: 'TokenRefresh'
          };

          if (rawDeviceInfo.isMobile) {
            // Mobile device formatting
            formattedDeviceInfo.mobile = JSON.stringify(rawDeviceInfo);
          } else {
            // Web browser formatting
            formattedDeviceInfo.web = {
              userAgent: rawDeviceInfo.userAgent,
              platform: rawDeviceInfo.platform,
              screen: rawDeviceInfo.screen,
              cookieEnabled: rawDeviceInfo.cookieEnabled,
              onLine: rawDeviceInfo.onLine
            };
          }

          await this.userService.logUserDeviceInfo(formattedDeviceInfo);
        } catch (error) {
          console.error('[OktaAuth] Error logging TokenRefresh event:', error);
        }
      }

      // Re-mark as initialized after successful refresh
      this.tokensInitialized = true;
      return true; // Success
    } catch (error: any) {
      // Re-mark as initialized on error
      this.tokensInitialized = true;

      console.error('[OktaAuth] Token refresh failed:', error);

      // Check if it's a network error or temporary issue
      if (error.status === 0 || error.status === 408 || error.message?.includes('network')) {
        // Network error - don't logout, let caller handle it
        console.warn('[OktaAuth] Token refresh failed due to network error - keeping user logged in');
        // Show diagnostics for network errors (don't logout, just inform)
        await this.showNetworkErrorDiagnostics('Network error during token refresh', error, 'OktaAuthService.refreshAccessToken:563');
        return false;
      }

      // Only logout for invalid_grant - this specifically means refresh token is expired/revoked.
      // By the time we get here we've ALREADY re-synced from native storage and retried once (see the
      // #2 loop above), so a surviving invalid_grant means the token is genuinely dead — not a
      // rotation race. Per OAuth 2.0, invalid_grant is the definitive "refresh token no longer valid".
      if (error.error?.error === 'invalid_grant') {
        console.error('[OktaAuth] Token refresh failed - invalid_grant after resync+retry (refresh token expired/revoked) - logging out');
        this.sessionRejected = true;
        await this.handleLogout('Token refresh failed: invalid_grant (refresh token expired/revoked)', 'OktaAuthService.refreshAccessToken:549');
        return false;
      }

      // For 401/403 errors WITHOUT invalid_grant, don't logout - could be server issues, rate limiting, etc.
      // The refresh token might still be valid, and we have retry logic in the caller
      if (error.status === 401 || error.status === 403) {
        console.warn('[OktaAuth] Token refresh failed with auth error (401/403) but no invalid_grant - keeping user logged in, tokens preserved for retry');
        // Show diagnostics for auth errors without invalid_grant
        await this.showNetworkErrorDiagnostics('Auth error (401/403) without invalid_grant - possible server issue', error, 'OktaAuthService.refreshAccessToken:579');
        return false;
      }

      // For other errors, don't logout but return failure
      console.warn('[OktaAuth] Token refresh failed with unexpected error - keeping user logged in');
      // Show diagnostics for unexpected errors
      await this.showNetworkErrorDiagnostics('Unexpected error during token refresh', error, 'OktaAuthService.refreshAccessToken:585');
      return false;
    } finally {
      // Always release the cross-process refresh lock we took, on every success/failure/return path.
      if (lockAcquired) {
        await this.releaseNativeRefreshLock();
      }
    }
  }

  /**
   * Sync tokens from iOS Keychain (written by background task) to Okta tokenManager
   * This should be called on cold start and app resume to pick up background-refreshed tokens
   * iOS only - no-op on other platforms
   */
  async syncTokensFromKeychain(): Promise<boolean> {
    // Only run on iOS
    if (Capacitor.getPlatform() !== 'ios') {
      return false;
    }

    // The user logged out — do not restore a session from the Keychain on this cold start/resume.
    // (Set in handleLogout, cleared on the next completed login in storeTokens.) This is what stops
    // the post-logout reload from resurrecting a dead session and landing on /mainmenu with no menu.
    if (await this.wasLoggedOut()) {
      return false;
    }

    // Mark tokens as not initialized during sync to prevent guard race condition
    this.tokensInitialized = false;

    try {
      const tokens = await BackgroundTokenRefresh.getTokensFromKeychain();

      if (!tokens.accessToken || !tokens.idToken || !tokens.refreshToken) {
        return false;
      }

      // Parse the JSON strings from Keychain
      const accessTokenData = JSON.parse(tokens.accessToken);
      const idTokenData = JSON.parse(tokens.idToken);
      const refreshTokenData = JSON.parse(tokens.refreshToken);

      // Check if these tokens are different from what's in tokenManager
      const currentAccessToken: any = await this.oktaAuth.tokenManager.get('accessToken');
      const currentRefreshToken: any = await this.oktaAuth.tokenManager.get('refreshToken');

      // Recency guard: only adopt the Keychain copy if it is NEWER than what we already hold.
      // Without this, a stale Keychain token (e.g. left over from an older build, or written by a
      // background refresh that has since been superseded by a foreground refresh) could clobber a
      // fresher token and trigger an invalid_grant logout. If we already have a token that is at
      // least as fresh, skip adoption — the next foreground refresh re-seeds the Keychain anyway.
      const keychainAccessExpiry = accessTokenData?.expiresAt ?? 0;
      const currentAccessExpiry = currentAccessToken?.expiresAt ?? 0;
      if (currentAccessToken && keychainAccessExpiry <= currentAccessExpiry) {
        return false;
      }

      const accessTokenChanged = !currentAccessToken || currentAccessToken.accessToken !== accessTokenData.accessToken;
      const refreshTokenChanged = !currentRefreshToken || currentRefreshToken.refreshToken !== refreshTokenData.refreshToken;

      if (accessTokenChanged || refreshTokenChanged) {
        // Don't adopt Keychain tokens into the tokenManager if a logout is underway — it would
        // repopulate storage right after handleLogout cleared it (the post-background/foreground
        // repopulation that lands on /mainmenu with a dead session).
        if (this.isLoggingOut) {
          return false;
        }

        // Update tokenManager with tokens from Keychain
        // CRITICAL: Must await - iOS Keychain sync is slow (200-500ms on older devices)
        await this.oktaAuth.tokenManager.setTokens({
          accessToken: accessTokenData,
          idToken: idTokenData,
          refreshToken: refreshTokenData
        });

        // Safety delay for storage flush (especially critical on iOS with slow Keychain I/O)
        await new Promise(resolve => setTimeout(resolve, 150));

        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('[OktaAuth] Failed to sync tokens from Keychain:', error);
      return false;
    } finally {
      // Always mark as initialized after sync attempt (success or failure) to prevent deadlock
      this.tokensInitialized = true;
    }
  }

  /**
   * "Producer" half of the native background-refresh scheme: write the current Okta tokens into the
   * store the OS background worker reads, so (1) the worker can actually refresh, and (2) a later
   * resume-sync has a token at least as fresh as ours to adopt (the recency guards in
   * syncTokensFrom* then prevent a stale copy from clobbering us).
   *  - iOS: write to the Keychain as JSON strings of the Okta token objects (the keys/format the
   *    BGTask and getTokensFromKeychain() expect). This is the seed iOS never had.
   *  - Android: hand the token objects to saveTokens() for the WorkManager. Called on login AND on
   *    every foreground refresh so the native copy can't drift stale behind a rotated token.
   * No-op off native. Best-effort: never throws into the caller's token-store/refresh flow.
   */
  private async seedNativeBackgroundTokens(accessToken: any, idToken: any, refreshToken: any): Promise<void> {
    const platform = Capacitor.getPlatform();
    try {
      if (platform === 'ios') {
        await BackgroundTokenRefresh.saveTokensToKeychain({
          accessToken: JSON.stringify(accessToken),
          idToken: JSON.stringify(idToken),
          refreshToken: JSON.stringify(refreshToken)
        });
      } else if (platform === 'android') {
        await BackgroundTokenRefresh.saveTokens({ accessToken, idToken, refreshToken });
      }
    } catch (error) {
      console.warn('[OktaAuth] Failed to seed native background-refresh tokens:', error);
    }
  }

  // Retrieve tokens from Capacitor Preferences
  async getStoredTokens() {
    const accessToken = this.authStorageService.getItem(AuthStorageKeys.Okta_accessToken);
    const refreshToken = this.authStorageService.getItem(AuthStorageKeys.Okta_refreshToken);

    if (accessToken && refreshToken) {
      return { accessToken: JSON.parse(accessToken), refreshToken: JSON.parse(refreshToken) };
    } else {
      return null;
    }
  }

  // Persisted logout flag (Capacitor Preferences). Read directly from Preferences (not the in-memory
  // cache) so it's reliable even on a fast resume before storage has finished loading. Set on logout,
  // cleared only on the next COMPLETED login (storeTokens) — not on login intent, so backing out of
  // the Okta screen can't leave a window where a stale Keychain copy resurrects the dead session.
  private async persistLoggedOut(loggedOut: boolean): Promise<void> {
    try {
      if (loggedOut) {
        await Preferences.set({ key: AuthStorageKeys.Okta_LoggedOut, value: 'true' });
      } else {
        await Preferences.remove({ key: AuthStorageKeys.Okta_LoggedOut });
      }
    } catch (error) {
      console.warn('[OktaAuth] Failed to update logged-out flag:', error);
    }
  }

  private async wasLoggedOut(): Promise<boolean> {
    try {
      const { value } = await Preferences.get({ key: AuthStorageKeys.Okta_LoggedOut });
      return value === 'true';
    } catch {
      return false;
    }
  }

  // Clear the local session WITHOUT ending the Okta SSO session or redirecting anywhere.
  //
  // Used when the tokens are valid but unusable — currently only when login succeeded at Okta but
  // the backend has no profile to link the account to. handleLogout() can't be used there: it
  // redirects to Okta's /logout endpoint, which tears down the page before the user can read why
  // their login failed.
  //
  // On native, tokenManager.clear() alone is NOT enough. storeTokens() also seeds the iOS Keychain
  // and Android EncryptedSharedPreferences copies, and the next cold start would restore them via
  // syncTokensFromKeychain / syncTokensFromNativeStorage — landing the user on /mainmenu with a
  // session that still has no profile behind it. So cancel background refresh and set the persisted
  // logged-out flag, same as handleLogout does.
  //
  // Deliberately does NOT set isLoggingOut: there's no redirect teardown window to protect here, and
  // leaving it set would block the very next login attempt from persisting its tokens.
  async clearUnusableSession(reason: string): Promise<void> {
    console.warn(`[OktaAuth] Clearing unusable local session: ${reason}`);

    this.globalPollingService.disconnect();

    if (Capacitor.isNativePlatform()) {
      try {
        await BackgroundTokenRefresh.cancelBackgroundRefresh();
      } catch (error) {
        console.warn('[BG] Failed to cancel background refresh:', error);
      }
    }

    try {
      this.getOktaAuth().tokenManager.clear();
    } catch (error) {
      console.warn('[OktaAuth] tokenManager.clear() failed:', error);
    }

    await this.hybridStorageService.clear();
    await this.authStorageService.clear();
    await this.appService.clearAll();
    await this.persistLoggedOut(true);

    this.sessionRejected = false;
    this.setIsLoggingIn(false);
  }

  // Handle logout and clear all session data
  async handleLogout(reason?: string, callSite?: string) {
    // Stop polling to prevent 401 errors after logout
    // This disconnects the polling timer and stops all background HTTP requests
    this.globalPollingService.disconnect();
    
    // Show diagnostics if debug mode enabled
    await this.showLogoutDiagnostics(reason || 'Manual logout', callSite || 'OktaAuthService.handleLogout');


    // Clear any active impersonation before logging out
    try {
      await firstValueFrom(this.http.post(`${this.runtimeConfig.currentConfig.apiUrl}/UserProfile/ClearImpersonationAsync`, {}));
    } catch (error) {
      console.warn('Failed to clear impersonation during logout:', error);
    }

    // Call backend to clear server-side cache (roles, userId, etc.)
    try {
      await firstValueFrom(this.http.post(`${this.runtimeConfig.currentConfig.apiUrl}/OktaAuth/logout`, {}));
    } catch (error) {
      // Don't block logout if backend call fails
      console.warn('Failed to clear backend cache during logout:', error);
    }

    // From here on, block every token-persistence path. Any refresh/sync that was started before
    // logout (app-resume refresh, HTTP interceptor, guard, Keychain/native sync) and resolves during
    // or after the clears below must NOT re-seed storage — otherwise the post-logout cold start reads
    // those tokens back in and lands on /mainmenu with a dead session. Set before clearing anything.
    this.isLoggingOut = true;

    // Cancel iOS & Android background token refresh (clears Keychain / EncryptedSharedPreferences)
    if (Capacitor.isNativePlatform()) {
      try {
        await BackgroundTokenRefresh.cancelBackgroundRefresh();

      } catch (error) {
        console.warn('[BG] Failed to cancel background refresh:', error);
      }
    }

    // Capture tokens BEFORE clearing local storage. signOut() builds its Okta /logout redirect from
    // the idToken (id_token_hint); if the idToken is gone it falls back to an XHR closeSession() that
    // can't reach the browser's SSO cookie on native, leaving the session alive — so the next login
    // silently auto-authenticates. We pass the captured tokens explicitly since the tokenManager is
    // cleared just below: idToken -> id_token_hint (ends the SSO session), access/refresh -> revoked.
    let idToken: any, accessToken: any, refreshToken: any;
    try {
      const tokenManager = this.getOktaAuth().tokenManager;
      idToken = await tokenManager.get('idToken');
      accessToken = await tokenManager.get('accessToken');
      refreshToken = await tokenManager.get('refreshToken');
    } catch (error) {
      console.warn('[OktaAuth] Failed to read tokens before logout:', error);
    }

    // Clear all local tokens/state BEFORE signOut. On native, signOut() redirects the webview to
    // Okta's logout endpoint, and any awaited work after it is NOT guaranteed to run before the
    // page tears down. If Preferences still holds okta-token-storage, the post-logout cold start
    // repopulates the tokenManager and lands on /mainmenu with a dead session (no menu items).
    try {
      this.getOktaAuth().tokenManager.clear();
    } catch (error) {
      console.warn('[OktaAuth] tokenManager.clear() failed during logout:', error);
    }
    await this.hybridStorageService.clear();
    await this.authStorageService.clear();
    await this.appService.clearAll();

    // Persist a logged-out flag that survives the signOut redirect/reload. The post-logout cold start
    // checks this before restoring a session (syncTokensFromKeychain / syncTokensFromNativeStorage),
    // so a Keychain/native copy can't resurrect a dead session on reopen — even if its native clear
    // didn't run. Awaited so it's flushed before signOut() redirects. Cleared on the next login.
    await this.persistLoggedOut(true);

    // signOut LAST — redirects to Okta's /logout endpoint to end the SSO session, using the captured
    // tokens (the tokenManager is now empty). Shared with endOktaSsoSession (login-error retry).
    await this.redirectToOktaLogout(idToken, accessToken, refreshToken);
  }

  // Redirect to Okta's /logout endpoint to end the SSO session, using tokens captured before the local
  // session was cleared (the tokenManager is empty by this point, so they're passed explicitly).
  //
  // Android: login runs in a Chrome Custom Tab (see launchAuthInCustomTab), so the Okta SSO session
  // cookie lives in Chrome's cookie jar — NOT the System WebView. The SDK's signOut() ends the
  // session via window.location.assign() inside the WebView, which can't see that cookie, so the
  // session survives and the next login silently re-authenticates (the user is bounced straight back
  // into the app right after "logging out"). Run the /logout redirect in a Custom Tab instead so it
  // hits the same cookie jar login used. iOS routes Okta to SFSafariViewController natively for both
  // login and logout (AppDelegate.PermanentWebViewDelegate intercepts the WebView navigation), and
  // web navigates normally — so both keep the standard signOut() path below.
  private async redirectToOktaLogout(idToken: any, accessToken: any, refreshToken: any): Promise<void> {
    if (Capacitor.getPlatform() === 'android') {
      await this.signOutViaCustomTab(idToken, accessToken, refreshToken);
    } else {
      try {
        await this.getOktaAuth().signOut({
          postLogoutRedirectUri: Capacitor.isNativePlatform() ? this.getSignOutRedirectURI() : window.location.origin,
          idToken,
          accessToken,
          refreshToken
        });
      } catch (error) {
        // signOut threw BEFORE redirecting (e.g. token revocation failed while offline). No redirect
        // means no page reload, so initializeOktaAuth won't run to reset the guard — clear it here so
        // normal token handling can resume. Safe specifically because no redirect is imminent: there's
        // no page-teardown window for an in-flight refresh to repopulate into (and the local session is
        // already cleared above, so there's nothing to protect). The successful-redirect path leaves
        // the flag set and relies on the reload's initializeOktaAuth to reset it.
        console.warn('[OktaAuth] signOut failed before redirect — clearing logout guard:', error);
        this.isLoggingOut = false;
      }
    }
  }

  // Read the current tokens so the Okta SSO session can be ended AFTER the local session is cleared.
  // The login-error screen captures these before clearUnusableSession() empties the tokenManager, then
  // hands them to endOktaSsoSession() if the user chooses to fully sign out. Best-effort.
  async captureTokensForSignOut(): Promise<{ idToken: any; accessToken: any; refreshToken: any }> {
    try {
      const tokenManager = this.getOktaAuth().tokenManager;
      return {
        idToken: await tokenManager.get('idToken'),
        accessToken: await tokenManager.get('accessToken'),
        refreshToken: await tokenManager.get('refreshToken')
      };
    } catch (error) {
      console.warn('[OktaAuth] Failed to capture tokens for sign-out:', error);
      return { idToken: undefined, accessToken: undefined, refreshToken: undefined };
    }
  }

  // End ONLY the Okta SSO session, using tokens captured before the local session was cleared.
  //
  // The login-error screen clears the local/native session immediately (clearUnusableSession) so a
  // cold start can't resurrect a profile-less session, but deliberately leaves the Okta SSO cookie
  // intact so a logout redirect doesn't tear down the error message. That intact cookie is what makes
  // a retry silently re-authenticate as the SAME rejected identity (no password prompt) and fail
  // identically — trapping a user who picked the wrong login type or whose account isn't provisioned.
  // When the user chooses "try a different sign-in", this ends that SSO session so the next attempt
  // lands on a real login prompt. Mirrors handleLogout's redirect, minus the local clears (done).
  async endOktaSsoSession(captured: { idToken: any; accessToken: any; refreshToken: any }): Promise<void> {
    this.isLoggingOut = true;
    await this.persistLoggedOut(true);
    await this.redirectToOktaLogout(captured.idToken, captured.accessToken, captured.refreshToken);
  }

  // Android only — end the Okta SSO session in the SAME Chrome Custom Tab that login used.
  // Mirrors the SDK's signOut(): revoke the refresh/access tokens, then redirect to Okta's /logout
  // endpoint (id_token_hint ends the SSO session; post_logout_redirect_uri = com.nhaschools.app:/logout
  // brings the user back to the app). The one critical difference from signOut() is WHERE the redirect
  // runs — a Custom Tab (Chrome's cookie jar, where the SSO cookie actually lives) instead of
  // window.location.assign in the System WebView (a separate cookie jar that never held the cookie).
  // Without this the session survives logout and the next login auto-authenticates.
  private async signOutViaCustomTab(idToken: any, accessToken: any, refreshToken: any): Promise<void> {
    const oktaAuth = this.getOktaAuth();

    // Revoke tokens explicitly — signOut() normally does this, but we're bypassing it. Best-effort:
    // a failed/offline revoke must not block ending the SSO session (local tokens are already cleared).
    try {
      if (refreshToken) await oktaAuth.revokeRefreshToken(refreshToken);
      if (accessToken) await oktaAuth.revokeAccessToken(accessToken);
    } catch (error) {
      console.warn('[OktaAuth] Token revocation during logout failed (continuing):', error);
    }

    // Build the Okta /logout URL from the captured idToken (the tokenManager is already cleared, so
    // the SDK can't read it back itself — we pass it in explicitly).
    let logoutUri = '';
    try {
      logoutUri = (oktaAuth as any).getSignOutRedirectUrl({
        idToken,
        postLogoutRedirectUri: this.getSignOutRedirectURI()
      });
    } catch (error) {
      console.warn('[OktaAuth] Failed to build logout URL:', error);
    }

    if (!logoutUri) {
      // No idToken to build the redirect (e.g. storage was already cleared). The local session is
      // gone; nothing more to do — clear the guard so normal token handling can resume.
      this.isLoggingOut = false;
      return;
    }

    // Open in the Custom Tab (Chrome) so the SSO cookie is actually cleared. Flag it so the
    // logout-callback deep link can dismiss the tab (DeepLinkService.handleDeepLink → closeAuthBrowser).
    this.isAuthBrowserOpen = true;
    try {
      await Browser.open({ url: logoutUri });
    } catch (error) {
      // Custom Tab unavailable — fall back to the WebView redirect so logout is never fully blocked.
      // (The SSO cookie may persist in this rare edge case, but local tokens are already cleared.)
      console.warn('[OktaAuth] Failed to open Custom Tab for logout; falling back to WebView:', error);
      this.isAuthBrowserOpen = false;
      window.location.assign(logoutUri);
    }
  }

  isTokenExpired(token: any): boolean {
    if (!token || !token.expiresAt) {
      return true; // Treat missing or invalid token as expired
    }
    const expirationTime = token.expiresAt * 1000; // Convert to milliseconds
    return Date.now() > expirationTime;
  }      

  async handleOktaRedirect(url: string) {
    if (this.isHandlingRedirect) return;

    this.isHandlingRedirect = true;

    try {
      const oktaAuth = this.getOktaAuth();
      const isAuthenticated = await oktaAuth.isAuthenticated();

      if (!isAuthenticated) {
        const code = UrlHelper.getUrlParameter('code', url);
        const codeVerifier = this.getCodeVerifier();
        loginDebugAlert('1) Callback reached', 'code=' + (code ? 'yes' : 'NO') + '   verifier=' + (codeVerifier ? ('yes (' + codeVerifier.length + ' chars)') : 'MISSING'));

        if (code) {
          if (!codeVerifier) {
            loginDebugAlert('X) Missing code_verifier', 'The PKCE verifier was not in storage, so the exchange cannot run. This is the failure point.');
            throw new Error('Missing code_verifier for PKCE flow');
          }

          const params = new URLSearchParams({
              client_id: this.getClientId(),
              grant_type: 'authorization_code',
              redirect_uri: await this.getRedirectUriForLoginType(),
              code: code,
              code_verifier: codeVerifier,
              oktaTokenURL: this.getTokenEndpoint()
          });

          
          let response = await this.handleTokenExchange(params);
          
          await this.storeTokens(response);
          
        }
      }
    }
    catch (_error: any) {
      console.error('[OktaAuth] handleOktaRedirect error:', _error?.message || _error);
      throw _error; // Re-throw so LoginCallbackComponent catch can display it
    } finally {
      this.isHandlingRedirect = false;
    }
  }

  // Android only — invoked by the SDK's setLocation hook (see initializeOktaAuth). Opens the Okta
  // authorize URL in a Chrome Custom Tab (real Chrome engine → WebAuthn/passkeys work) instead of
  // navigating the System WebView. Fire-and-forget: setLocation is synchronous (loc: string) => void.
  // If the Custom Tab cannot be opened, fall back to navigating the WebView so login is never blocked
  // (the user may hit the WebAuthn limitation, but that is strictly better than no login at all).
  private launchAuthInCustomTab(url: string): void {
    this.isAuthBrowserOpen = true;
    // Durably persist the PKCE transaction + login type BEFORE handing off to the Custom Tab.
    // The SDK saved them via authStorageService.setItem (memory + a fire-and-forget Preferences
    // write) then we background the app to Chrome. On a low-memory device the OS can kill us before
    // that async write flushes, and the WebView reload on return wipes the in-memory copy - so the
    // callback would read nothing and fail with "Missing code_verifier". Awaiting an explicit flush
    // here closes that window. Best-effort: flushAuthStateToDisk never rejects, so login always
    // proceeds to open the browser even if the write fails.
    this.flushAuthStateToDisk()
      .then(() => Browser.open({ url }))
      .catch((error) => {
        console.error('[OktaAuth] Failed to open Custom Tab for login; falling back to WebView:', error);
        this.isAuthBrowserOpen = false;
        window.location.assign(url);
      });
  }

  // Force the current auth transaction + login type from the in-memory auth store out to durable
  // Capacitor Preferences and AWAIT the write, so an OS kill while the Custom Tab is foregrounded
  // can't lose the PKCE code_verifier (see launchAuthInCustomTab). Android-only path; best-effort -
  // swallows any failure so it can never block a login.
  private async flushAuthStateToDisk(): Promise<void> {
    try {
      const entries: Array<[string, string | null]> = [
        ['okta-transaction-storage', this.authStorageService.getItem('okta-transaction-storage')],
        [AuthStorageKeys.Okta_LoginType, this.authStorageService.getItem(AuthStorageKeys.Okta_LoginType)],
      ];
      await Promise.all(
        entries
          .filter(([, value]) => value != null)
          .map(([key, value]) => Preferences.set({ key, value: value as string }))
      );
    } catch (error) {
      console.warn('[OktaAuth] Failed to durably flush auth state before Custom Tab:', error);
      // Best-effort: swallow so the login still proceeds.
    }
  }

  // Dismiss the Custom Tab opened by launchAuthInCustomTab once Okta has redirected back via the
  // com.nhaschools.app:/callback deep link. Called from DeepLinkService when it sees the auth callback.
  // No-op on iOS/web (the flag is only ever set on Android), and safe if the tab is already gone.
  async closeAuthBrowser(): Promise<void> {
    if (!this.isAuthBrowserOpen) {
      return;
    }
    this.isAuthBrowserOpen = false;
    try {
      await Browser.close();
    } catch (error) {
      console.warn('[OktaAuth] Browser.close() failed (Custom Tab may already be closed):', error);
    }
  }

  // determine if the auth token is 10 minutes or less from expiring by comparing the current time with the token's expiration time
  public async refreshTokenAfterXMins(numberOfMins: number): Promise<void> {
    const accessToken = await this.oktaAuth.tokenManager.get('accessToken');
    const refreshToken = await this.oktaAuth.tokenManager.get('refreshToken');
    const isNative = Capacitor.isNativePlatform();

    if (accessToken && !this.oktaAuth.tokenManager.hasExpired(accessToken)) {
      const expiresIn = (accessToken.expiresAt * 1000) - Date.now();
      if (expiresIn < numberOfMins * 60 * 1000) { // less than 10 minutes
        this.refreshAccessToken(refreshToken).then((success) => {
          if (!success) {
            console.warn('[OktaAuth] Token refresh failed in refreshTokenAfterXMins - may need manual login');
          }
        }).catch(async (_error) => {
          console.error('[OktaAuth] Unexpected error in refreshTokenAfterXMins:', _error);
          // Show diagnostics for unexpected errors during proactive token refresh
          await this.showTokenRefreshErrorDiagnostics('refreshTokenAfterXMins - Unexpected error during proactive token refresh', _error);
        });
    }
    } else if (isNative) {
      // If access token is missing/expired but refresh token is valid, refresh tokens (native only)
      if (refreshToken && !this.oktaAuth.tokenManager.hasExpired(refreshToken)) {
        const success = await this.refreshAccessToken(refreshToken);
        if (!success) {
          console.warn('[OktaAuth] Token refresh failed for expired access token');
          // Don't logout - refreshAccessToken already handles auth errors
        }
      } else {
        await this.handleLogout('refreshTokenAfterXMins (Mobile): Refresh token expired or missing', 'OktaAuthService.refreshTokenAfterXMins:771');
      }
    } else {
      // Web: Access token expired/missing - try refresh token before logging out
      // This fixes the issue where web users get logged out after tabbing away and coming back
      // even when their refresh token (7-day expiry) is still valid
      if (refreshToken && !this.oktaAuth.tokenManager.hasExpired(refreshToken)) {

        const success = await this.refreshAccessToken(refreshToken);
        if (!success) {
          console.warn('[OktaAuth] Web - token refresh failed, may need to login');
          // Don't logout - refreshAccessToken already handles auth errors (401, 403, invalid_grant)
          // For network errors, tokens are preserved
        }
      } else {

        await this.handleLogout('refreshTokenAfterXMins (Web): Refresh token expired or missing', 'OktaAuthService.refreshTokenAfterXMins:787');
      }
    }
  }
  
  private getCodeVerifier(): string {
    // Try to get from Okta's transaction manager first (uses authStorageService on native)
    const transactionMeta = this.oktaAuth.transactionManager.load();
    // TransactionMeta may not have a strongly-typed codeVerifier property, so check the property existence first
    if (transactionMeta && 'codeVerifier' in transactionMeta && (transactionMeta as any).codeVerifier) {
      
      return (transactionMeta as any).codeVerifier;
    }

    // Fallback to direct storage access
    const storageValue = this.authStorageService.getItem('okta-transaction-storage');
    if (storageValue) {
      try {
        const parsed = JSON.parse(storageValue);
        if (parsed?.codeVerifier) {
          
          return parsed.codeVerifier;
        }
      } catch (_error) {
        
      }
    }

    
    return '';
  }


  /**
   * Verify that tokens were successfully loaded into Okta tokenManager
   * This helps catch cases where storage was ready but tokens weren't properly loaded
   */
  private async verifyTokensLoaded(): Promise<void> {
    try {
      const accessToken = await this.oktaAuth.tokenManager.get('accessToken');
      const refreshToken = await this.oktaAuth.tokenManager.get('refreshToken');

      if (!accessToken && !refreshToken) {
        // Only log if we expected tokens but didn't find them (potential issue)
        // Silent on first launch or after logout
      }
    } catch (error) {
      console.error('[OktaAuth] Error verifying tokens:', error);
    }
  }

  private async handleTokenExchange(params: URLSearchParams): Promise<any> {
    const queryParams = {
      oktaTokenURL: params.get('oktaTokenURL') || '',
      redirectUri: params.get('redirect_uri') || '',
      code: params.get('code') || '',
      codeVerifier: params.get('code_verifier') || '',
      clientId: params.get('client_id') || ''
    };

    const url = new URL(this.apiUrl);
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value) url.searchParams.append(key, value);
    });

    const fullUrl = url.toString();
    console.warn('[TokenExchange] calling:', url.origin + url.pathname);

    // The authorization code is single-use. Retry ONLY when the code could not have been
    // consumed: a transport failure reaching our backend, or a 429/5xx where the token endpoint
    // never processed it. A 4xx (e.g. invalid_grant) means the code is spent - retrying is futile
    // and would just mask the real cause, so we stop immediately on those.
    let lastError: any = null;
    for (let attempt = 1; attempt <= 3; attempt++) {
      let response: any;
      try {
        response = await CapacitorHttp.get({ url: fullUrl });
      } catch (networkError: any) {
        // No response = backend unreachable; the code was not consumed, safe to retry.
        lastError = networkError;
        if (attempt < 3) {
          await new Promise(res => setTimeout(res, 500 * attempt));
          continue;
        }
        break;
      }

      if (response.status === 200) {
        let data = response.data;
        if (typeof data === 'string') {
          data = JSON.parse(data);
        }
        loginDebugAlert('2) Token exchange OK (200)', 'org: ' + url.origin);
        return data;
      }

      lastError = new Error(`Token exchange failed with status ${response.status} for ${url.origin}`);
      const retryable = response.status === 429 || response.status >= 500;
      if (!retryable || attempt === 3) {
        break;
      }
      await new Promise(res => setTimeout(res, 500 * attempt));
    }

    loginDebugAlert('X) Token exchange FAILED', (lastError?.message || String(lastError)) + '\norg: ' + url.origin);
    throw new Error(`TokenExchange to ${this.apiUrl}: ${lastError?.message || lastError}`);
  }

  /**
   * Show logout diagnostics modal if debug mode is enabled
   * Displays reason for logout, network state, token state, and device info
   */
  private async showLogoutDiagnostics(reason: string, callSite: string): Promise<void> {
    if (!this.loginDebugService.isDebugModeEnabled) {
      return;
    }

    try {
      const deviceInfo = await Device.getInfo();
      const networkStatus = await Network.getStatus();
      const accessToken: any = await this.oktaAuth.tokenManager.get('accessToken');
      const refreshToken: any = await this.oktaAuth.tokenManager.get('refreshToken');

      const diagnostics: any = {
        timestamp: new Date().toISOString(),
        authType: 'Logout',
        callSite: callSite,
        networkType: networkStatus.connectionType,
        networkAvailable: networkStatus.connected,
        networkSwitching: false,
        retriesUsed: 0,
        maxRetries: 0,
        success: false, // Logout is always a failure scenario
        platform: deviceInfo.platform,
        osVersion: deviceInfo.osVersion,
        modelName: deviceInfo.model,
        hadCachedProfile: !!this.userService.CurrentUserProfile,
        refreshTokenValid: refreshToken && !this.oktaAuth.tokenManager.hasExpired(refreshToken),
        accessTokenValid: accessToken && !this.oktaAuth.tokenManager.hasExpired(accessToken),
        error: reason // The reason for logout
      };

      // Check if mobile - show fullscreen modal
      const isMobile = window.innerWidth <= 768;
      this.matDialog.open(LoginDiagnosticsModalComponent, {
        width: isMobile ? '100vw' : '600px',
        maxWidth: isMobile ? '100vw' : '90vw',
        maxHeight: isMobile ? '100vh' : '90vh',
        height: isMobile ? '100vh' : 'auto',
        panelClass: isMobile ? 'fullscreen-mobile-dialog' : '',
        data: diagnostics
      });
    } catch (error) {
      console.error('[LogoutDiagnostics] Failed to show diagnostics:', error);
    }
  }

  /**
   * Show diagnostics when user is redirected to login due to auth guard
   * Called by handleAuthRequired when user lacks valid tokens to access a protected route
   */
  private async showAuthRequiredDiagnostics(attemptedUrl: string): Promise<void> {
    if (!this.loginDebugService.isDebugModeEnabled) {
      return;
    }

    try {
      const deviceInfo = await Device.getInfo();
      const networkStatus = await Network.getStatus();
      const accessToken: any = await this.oktaAuth.tokenManager.get('accessToken');
      const refreshToken: any = await this.oktaAuth.tokenManager.get('refreshToken');

      const diagnostics: any = {
        timestamp: new Date().toISOString(),
        authType: 'Auth Required (Guard Redirect)',
        callSite: 'OktaAuthService.handleAuthRequired:192',
        networkType: networkStatus.connectionType,
        networkAvailable: networkStatus.connected,
        networkSwitching: false,
        retriesUsed: 0,
        maxRetries: 0,
        success: false, // Auth required is a failure scenario
        platform: deviceInfo.platform,
        osVersion: deviceInfo.osVersion,
        modelName: deviceInfo.model,
        hadCachedProfile: !!this.userService.CurrentUserProfile,
        refreshTokenValid: refreshToken && !this.oktaAuth.tokenManager.hasExpired(refreshToken),
        accessTokenValid: accessToken && !this.oktaAuth.tokenManager.hasExpired(accessToken),
        error: `Auth required - redirected from: ${attemptedUrl}. No valid tokens to access protected route.`
      };

      // Check if mobile - show fullscreen modal
      const isMobile = window.innerWidth <= 768;
      this.matDialog.open(LoginDiagnosticsModalComponent, {
        width: isMobile ? '100vw' : '600px',
        maxWidth: isMobile ? '100vw' : '90vw',
        maxHeight: isMobile ? '100vh' : '90vh',
        height: isMobile ? '100vh' : 'auto',
        panelClass: isMobile ? 'fullscreen-mobile-dialog' : '',
        data: diagnostics
      });
    } catch (error) {
      console.error('[AuthRequiredDiagnostics] Failed to show diagnostics:', error);
    }
  }

  /**
   * Show diagnostics when an unexpected error occurs during token refresh
   * Called from catch blocks in token refresh operations
   */
  private async showTokenRefreshErrorDiagnostics(reason: string, error: any): Promise<void> {
    if (!this.loginDebugService.isDebugModeEnabled) {
      return;
    }

    try {
      const deviceInfo = await Device.getInfo();
      const networkStatus = await Network.getStatus();
      const accessToken: any = await this.oktaAuth.tokenManager.get('accessToken');
      const refreshToken: any = await this.oktaAuth.tokenManager.get('refreshToken');

      const diagnostics: any = {
        timestamp: new Date().toISOString(),
        authType: 'Token Refresh Error',
        callSite: 'OktaAuthService.refreshTokenAfterXMins:761',
        networkType: networkStatus.connectionType,
        networkAvailable: networkStatus.connected,
        networkSwitching: false,
        retriesUsed: 0,
        maxRetries: 0,
        success: false,
        platform: deviceInfo.platform,
        osVersion: deviceInfo.osVersion,
        modelName: deviceInfo.model,
        hadCachedProfile: !!this.userService.CurrentUserProfile,
        refreshTokenValid: refreshToken && !this.oktaAuth.tokenManager.hasExpired(refreshToken),
        accessTokenValid: accessToken && !this.oktaAuth.tokenManager.hasExpired(accessToken),
        error: `${reason}: ${error?.message || error?.toString() || 'Unknown error'}`
      };

      // Check if mobile - show fullscreen modal
      const isMobile = window.innerWidth <= 768;
      this.matDialog.open(LoginDiagnosticsModalComponent, {
        width: isMobile ? '100vw' : '600px',
        maxWidth: isMobile ? '100vw' : '90vw',
        maxHeight: isMobile ? '100vh' : '90vh',
        height: isMobile ? '100vh' : 'auto',
        panelClass: isMobile ? 'fullscreen-mobile-dialog' : '',
        data: diagnostics
      });
    } catch (diagError) {
      console.error('[TokenRefreshErrorDiagnostics] Failed to show diagnostics:', diagError);
    }
  }

  /**
   * Show diagnostics for network errors and other non-logout errors during token refresh
   * User is NOT logged out, but we want to track why refresh failed
   */
  private async showNetworkErrorDiagnostics(reason: string, error: any, callSite: string): Promise<void> {
    if (!this.loginDebugService.isDebugModeEnabled) {
      return;
    }

    try {
      const deviceInfo = await Device.getInfo();
      const networkStatus = await Network.getStatus();
      const accessToken: any = await this.oktaAuth.tokenManager.get('accessToken');
      const refreshToken: any = await this.oktaAuth.tokenManager.get('refreshToken');

      const diagnostics: any = {
        timestamp: new Date().toISOString(),
        authType: 'Network/Server Error (No Logout)',
        callSite: callSite,
        networkType: networkStatus.connectionType,
        networkAvailable: networkStatus.connected,
        networkSwitching: false,
        retriesUsed: 0,
        maxRetries: 0,
        success: false,
        platform: deviceInfo.platform,
        osVersion: deviceInfo.osVersion,
        modelName: deviceInfo.model,
        hadCachedProfile: !!this.userService.CurrentUserProfile,
        refreshTokenValid: refreshToken && !this.oktaAuth.tokenManager.hasExpired(refreshToken),
        accessTokenValid: accessToken && !this.oktaAuth.tokenManager.hasExpired(accessToken),
        error: `${reason}: ${error?.message || error?.toString() || error?.status || 'Unknown error'}`
      };

      // Check if mobile - show fullscreen modal
      const isMobile = window.innerWidth <= 768;
      this.matDialog.open(LoginDiagnosticsModalComponent, {
        width: isMobile ? '100vw' : '600px',
        maxWidth: isMobile ? '100vw' : '90vw',
        maxHeight: isMobile ? '100vh' : '90vh',
        height: isMobile ? '100vh' : 'auto',
        panelClass: isMobile ? 'fullscreen-mobile-dialog' : '',
        data: diagnostics
      });
    } catch (diagError) {
      console.error('[NetworkErrorDiagnostics] Failed to show diagnostics:', diagError);
    }
  }
}
